import React, { useState, useEffect } from 'react';
import { testimonialsAPI } from '../../lib/api';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

export default function TestimonialsAdmin() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved'

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await testimonialsAPI.getAll();
      setTestimonials(response.testimonials);
    } catch (err) {
      console.error('Failed to fetch testimonials:', err);
      setError(err.message || 'Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await testimonialsAPI.approve(id);
      // Update local state
      setTestimonials(prev => 
        prev.map(testimonial => 
          testimonial._id === id ? { ...testimonial, featured: true } : testimonial
        )
      );
    } catch (err) {
      console.error('Failed to approve testimonial:', err);
      alert('Failed to approve testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      await testimonialsAPI.delete(id);
      // Update local state
      setTestimonials(prev => prev.filter(testimonial => testimonial._id !== id));
    } catch (err) {
      console.error('Failed to delete testimonial:', err);
      alert('Failed to delete testimonial');
    }
  };

  const filteredTestimonials = testimonials.filter(testimonial => {
    if (filter === 'all') return true;
    if (filter === 'pending') return !testimonial.featured;
    if (filter === 'approved') return testimonial.featured;
    return true;
  });

  const getCategoryColor = (world) => {
    switch (world) {
      case 'web3': return 'warning';
      case 'community': return 'success';
      case 'software': return 'primary';
      default: return 'accent';
    }
  };

  const getCategoryIcon = (world) => {
    switch (world) {
      case 'web3': return 'Zap';
      case 'community': return 'Users';
      case 'software': return 'Code';
      default: return 'Globe';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Testimonials Management</h1>
        <button 
          onClick={fetchTestimonials}
          className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
        >
          <Icon name="RefreshCw" size={16} className="mr-2" />
          Refresh
        </button>
      </div>

      <div className="mb-6">
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md ${
              filter === 'all' 
                ? 'bg-primary text-white' 
                : 'bg-surface text-text-secondary hover:bg-surface/80'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-md ${
              filter === 'pending' 
                ? 'bg-warning text-white' 
                : 'bg-surface text-text-secondary hover:bg-surface/80'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-md ${
              filter === 'approved' 
                ? 'bg-success text-white' 
                : 'bg-surface text-text-secondary hover:bg-surface/80'
            }`}
          >
            Approved
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Icon name="Loader" size={40} className="animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="bg-error/10 border border-error/30 rounded-lg p-6 text-center">
          <Icon name="AlertTriangle" size={32} className="text-error mx-auto mb-4" />
          <p className="text-error">{error}</p>
        </div>
      ) : filteredTestimonials.length === 0 ? (
        <div className="bg-surface rounded-lg p-6 text-center">
          <Icon name="FileX" size={32} className="text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary">No testimonials found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTestimonials.map(testimonial => (
            <div 
              key={testimonial._id}
              className="bg-surface rounded-lg border border-border p-6 shadow-elevation-1"
            >
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 mr-4">
                  <Image 
                    src={testimonial.avatar || 'https://via.placeholder.com/100'} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-medium text-primary">{testimonial.name}</h3>
                  <p className="text-sm text-text-secondary">{testimonial.position}</p>
                  <p className="text-xs text-text-secondary">{testimonial.company}</p>
                </div>
                <div className="ml-auto">
                  <div className={`flex items-center space-x-1 px-2 py-1 bg-${getCategoryColor(testimonial.world)}/10 rounded-full`}>
                    <Icon 
                      name={getCategoryIcon(testimonial.world)} 
                      size={12} 
                      className={`text-${getCategoryColor(testimonial.world)}`} 
                    />
                    <span className={`text-${getCategoryColor(testimonial.world)} text-xs capitalize`}>
                      {testimonial.world}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Icon 
                      key={i} 
                      name="Star" 
                      size={14} 
                      className={i < testimonial.rating ? "text-warning fill-current" : "text-border"} 
                    />
                  ))}
                </div>
                <p className="text-text-secondary text-sm line-clamp-3">
                  "{testimonial.content}"
                </p>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-border/20">
                <div className="text-xs text-text-secondary">
                  {new Date(testimonial.createdAt).toLocaleDateString()}
                </div>
                <div className="flex space-x-2">
                  {!testimonial.featured && (
                    <button
                      onClick={() => handleApprove(testimonial._id)}
                      className="p-2 rounded-full bg-success/10 text-success hover:bg-success/20 transition-colors"
                      title="Approve testimonial"
                    >
                      <Icon name="Check" size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(testimonial._id)}
                    className="p-2 rounded-full bg-error/10 text-error hover:bg-error/20 transition-colors"
                    title="Delete testimonial"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}